import React, { useEffect, useState } from "react";
import CardHeader from "./CardHeader/CardHeader";
import CardBody from "./CardBody/CardBody";
import GenericRadioGroup, { RadioGroupProps } from "../RadioGroup/GenericRadioGroup";
import { ReactComponent as ChevronUp } from "../../shared/assets/Icons/IconChevronUp.svg";
import { ReactComponent as ChevronDown } from "../../shared/assets/Icons/IconChevron-Down.svg";

/** Card variants representing different background themes. */
export type CardVariant = "primary" | "secondary" | "tertiary";

/** Tailwind utility classes for each variant. */
const variantClasses: Record<CardVariant, string> = {
  primary: `
    bg-semantic-bg-primary
    text-semantic-text-primary
    border border-semantic-border-primary
  `,
  secondary: `
    bg-semantic-bg-secondary
    text-semantic-text-secondary
    border border-semantic-border-secondary
  `,
  tertiary: `
    bg-semantic-bg-tertiary
    text-semantic-text-tertiary
    border border-semantic-border-tertiary
  `,
};

/** Available shadow levels for the card. */
export type CardShadow = "flat" | "sm" | "md";

/** Tailwind classes for the card shadow effects. */
const shadowClasses: Record<CardShadow, string> = {
  flat: "shadow-none",
  sm: "shadow-sm",
  md: "shadow-md",
};

/** Card props */
export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: CardVariant;
  shadow?: CardShadow;
  hasBorder?: boolean;
  isCollapsible: boolean;
  initialExpanded?: boolean;
  headerContent?: React.ReactNode;
  rightContent?: React.ReactNode;
  bodyContent?: React.ReactNode;
  radioOptions?: RadioGroupProps;
  theme?: "light" | "dark";
  children?: React.ReactNode;
  isLoading?: boolean;
  hoverable?: boolean;
  clickable?: boolean;
  actionsContent?: boolean;
  /** Optional header background color class (e.g., bg-semantic-bg-success, bg-semantic-bg-warning) */
  headerColor?: string;
  /** Apply custom scrollbar styles to scrollable content */
  useCustomScrollbar?: boolean;
  /** Max height for scrollable body content */
  maxBodyHeight?: string;
}

/**
 * The main card component that can include a header, body, and additional content.
 */
export const GenericCard: React.FC<CardProps> = ({
  variant = "primary",
  shadow = "flat",
  hasBorder = true,
  isCollapsible = false,
  initialExpanded,
  headerContent,
  rightContent,
  bodyContent,
  radioOptions,
  children,
  isLoading,
  hoverable = false,
  clickable = false,
  onClick,
  actionsContent = false,
  headerColor,
  className,
  useCustomScrollbar = false,
  maxBodyHeight,
  ...props
}) => {

  const startExpanded = initialExpanded !== undefined ? initialExpanded : true;
  const [isExpanded, setIsExpanded] = useState(startExpanded);
  // A card whose starting state changes starts over from it: a service-detail
  // card folded on a phone opens again when the screen widens past it (CTX-1421).
  useEffect(() => {
    setIsExpanded(startExpanded);
  }, [startExpanded]);
  // A card that cannot be collapsed always shows its body, whatever state it
  // was left in while it could.
  const showBody = isExpanded || !isCollapsible;

  const toggleCollapse = () => {
    if(isCollapsible) {
      setIsExpanded((prev) => !prev);
    }
  };

  let finalClasses = `
    w-full
    flex flex-col
    rounded-sm
    transition-all duration-200 ease-out
    ${variantClasses[variant]}
    ${shadowClasses[shadow]}
    ${hasBorder ? "border" : ""}
        ${bodyContent && (className?.includes('flex') || className?.includes('h-full')) ? 'overflow-clip' : ''}
  `;

    if (hoverable) {
      finalClasses += " transform hover:scale-105 hover:shadow-lg";
    }
    // add clickable
    if (clickable) {
      finalClasses += " cursor-pointer";
    }

    // Combine with custom className if provided
    const combinedClassName = className ? `${finalClasses} ${className}` : finalClasses;

    // Render a skeleton version if loading is true.
    if (isLoading) {
      return (
        <div className={combinedClassName} {...props}>
          <div className="animate-pulse p-4">
            <div className="h-6 bg-gray-300 rounded w-1/2 mb-4"></div>
            <div className="space-y-2">
              <div className="h-4 bg-gray-300 rounded w-full"></div>
              <div className="h-4 bg-gray-300 rounded w-3/4"></div>
              <div className="h-4 bg-gray-300 rounded w-full"></div>
            </div>
          </div>
        </div>
      );
    }

  return (
    <div       
      onClick={clickable ? onClick : undefined}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      className={combinedClassName} {...props}>
      {/* Header Section */}
      {headerContent && (
        <CardHeader
          onClick={toggleCollapse}
          hasDropdown={isCollapsible}
          headerColor={headerColor}
          rightContent={
            isCollapsible ? (
              actionsContent ? (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleCollapse();
                    }}
                    className="text-semantic-text-primary transition-transform"
                  >
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5" />
                    ) : (
                      <ChevronDown className="w-5 h-5" />
                    )}
                  </button>
                  {rightContent}
                </>
              ) : (
                <>
                  {rightContent}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleCollapse();
                    }}
                    className="text-semantic-text-primary transition-transform"
                  >
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5" />
                    ) : (
                      <ChevronDown className="w-5 h-5" />
                    )}
                  </button>
                </>
              )
            ) : (
              rightContent
            )
          }
        >
          {headerContent}
        </CardHeader>
      )}
      {/*
        Body Section

        An expanded body must never be capped below its own content height. This element is
        `overflow-visible` in the common case, so a max-height does not clip the content —
        the content keeps painting outside a box that stops short, and the siblings laid out
        after that box get overlapped. That is CTX-344: a Telco VRF card (a body well past
        the old 1000px cap) painted over the next VRF's header and the modal's Cancel/Save
        row. So: no cap when expanded.

        `maxBodyHeight` is the one case that still caps, and it is applied through the inline
        `style` below rather than a class — a Tailwind class assembled from a runtime value
        is never emitted by the compiler, so the class form silently did nothing.

        Consequence worth knowing: `max-height` cannot transition to or from `none`, so the
        expand/collapse reveal is now instant. The previous animation only ever looked right
        on bodies shorter than the cap anyway — a taller one animated to the cap and then
        popped the rest. Animating to the true content height needs a measured height
        (ref + ResizeObserver), which is a bigger change than this hotfix.
      */}
      <div
        className={`
          ${
            isCollapsible && !isExpanded ? "overflow-hidden" : 
            useCustomScrollbar && maxBodyHeight ? "overflow-y-auto custom-scrollbar" : 
            // `clip`, not `hidden`: it trims the same way but is not a scroll
            // container, so a sticky child (a phone table's pager) sticks to the
            // page's scroll instead of to this box (CTX-1379).
            (className?.includes('flex') || className?.includes('h-full')) ? "overflow-clip" : "overflow-visible"
          }
          transition-[max-height] duration-300
          ${(className?.includes('h-full') || className?.includes('flex')) ? '' : (showBody ? (maxBodyHeight ? '' : "max-h-none") : "max-h-0")}
          ${className?.includes('h-full') || className?.includes('flex') ? 'flex flex-1 min-h-0 flex-col' : ''}
        `}
        style={maxBodyHeight && showBody ? { maxHeight: maxBodyHeight } : undefined}
      >
        {bodyContent && <CardBody>{bodyContent}</CardBody>}
        {radioOptions && (
          <GenericRadioGroup
            options={radioOptions.options}
            defaultValue={radioOptions.defaultValue}
            onChange={radioOptions.onChange}
          />
        )}
        {children}
      </div>
    </div>
  );
};

export default GenericCard;
